import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { ServiceCenterDictionaryService } from './service-center-dictionary.service';
import { LoadingState } from '../../types/loading.type';
import { debounceTime, scan, shareReplay, Subject, switchMap } from 'rxjs';
import { IDictionaryItem } from '../../types/dictionary-item.interface';
import { TranslateModule } from '@ngx-translate/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { AsyncPipe } from '@angular/common';
import { MultiselectComponent } from '@ui-lib/components';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'mon-service-center-selector',
  imports: [TranslateModule, FormsModule, AsyncPipe, MultiselectComponent, ReactiveFormsModule],
  templateUrl: './service-center-selector.component.html',
  styleUrl: './service-center-selector.component.scss',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [ServiceCenterDictionaryService],
})
export class ServiceCenterSelectorComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);

  protected readonly serviceCenterDictionaryService = inject(ServiceCenterDictionaryService);

  @Input()
  public controlEntity: FormControl<string[]> = new FormControl([], { nonNullable: true });

  @Output()
  public selectionChange: EventEmitter<IDictionaryItem<string>[]> = new EventEmitter<
    IDictionaryItem<string>[]
  >();

  private loadedCount = 0;

  private readonly idNameMap: Map<string, string> = new Map<string, string>();

  private readonly keyword$ = new Subject<string>();

  protected readonly serviceCenters$ = this.keyword$.pipe(
    debounceTime(500),
    switchMap(() => {
      this.idNameMap.clear();

      return this.serviceCenterDictionaryService.source$.pipe(
        scan(
          (
            acc: IDictionaryItem<string>[],
            newbies: IDictionaryItem<string>[],
          ): IDictionaryItem<string>[] => {
            newbies.forEach((item): void => {
              this.idNameMap.set(item.id, item.name);
            });

            this.loadedCount = acc.length + newbies.length;

            return [...acc, ...newbies];
          },
          [],
        ),
      );
    }),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  public ngOnInit(): void {
    this.keyword$.next('');

    this.controlEntity.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((ids): void => {
        this.selectionChange.emit(
          ids.map(
            (id: string): IDictionaryItem<string> => ({
              id,
              name: this.idNameMap.get(id) ?? '-',
            }),
          ),
        );
      });
  }

  public loadMore(): void {
    if (this.loadedCount < this.serviceCenterDictionaryService.totalCount()) {
      this.serviceCenterDictionaryService.loadMore();
    }
  }

  protected readonly LoadingState = LoadingState;

  public onKeywordChange(keyword: string): void {
    this.serviceCenterDictionaryService.updateKeyword(keyword);
    this.keyword$.next(keyword);
  }
}
